import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { HiChevronDown } from "react-icons/hi";

import invoiceGenerate from "../../assets/svg/invoice-generate.svg";
import radioCheck from "../../assets/svg/radio-check.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  generateOppositionNoteOnHearingPdf,
  getOppositionNoteOnHearing,
  saveOppositionNoteOnHearing,
  type OppositionNoteOnHearingItem,
} from "../../api/opposition";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

export const OPPOSITION_NOTE_ON_HEARING_FORM_ID =
  "opposition-note-on-hearing-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const TEXTAREA_CLASS =
  "min-h-[80px] w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] resize-y";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

type Draft = {
  similarityNote1: string;
  similarityNote2: string;
  otherOppositionNote1: string;
  otherOppositionNote2: string;
};

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionNoteOnHearingView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [items, setItems] = useState<OppositionNoteOnHearingItem[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [activeIdx, setActiveIdx] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionNoteOnHearing(leadId)
      .then((data) => {
        if (cancelled) return;
        setItems(data.notes);
        const next: Record<string, Draft> = {};
        data.notes.forEach((it) => {
          next[it.conflictingMarkId] = {
            similarityNote1: it.similarityNote1 ?? "",
            similarityNote2: it.similarityNote2 ?? "",
            otherOppositionNote1: it.otherOppositionNote1 ?? "",
            otherOppositionNote2: it.otherOppositionNote2 ?? "",
          };
        });
        setDrafts(next);
        setActiveIdx(0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load Note on Hearing"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const activeItem = items[activeIdx] ?? null;
  const activeDraft = activeItem
    ? drafts[activeItem.conflictingMarkId] ?? null
    : null;

  const updateDraft = (patch: Partial<Draft>) => {
    if (!activeItem) return;
    setDrafts((prev) => ({
      ...prev,
      [activeItem.conflictingMarkId]: {
        ...prev[activeItem.conflictingMarkId],
        ...patch,
      },
    }));
  };

  const persistActive = async (): Promise<boolean> => {
    if (!leadId || !activeItem || !activeDraft) return false;
    try {
      const fresh = await saveOppositionNoteOnHearing(
        leadId,
        activeItem.conflictingMarkId,
        {
          similarityNote1: activeDraft.similarityNote1.trim() || null,
          similarityNote2: activeDraft.similarityNote2.trim() || null,
          otherOppositionNote1: activeDraft.otherOppositionNote1.trim() || null,
          otherOppositionNote2: activeDraft.otherOppositionNote2.trim() || null,
        }
      );
      setItems((prev) =>
        prev.map((it) =>
          it.conflictingMarkId === activeItem.conflictingMarkId ? fresh : it
        )
      );
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save Note"
      );
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    setSaving(true);
    try {
      const ok = await persistActive();
      if (ok) {
        toast.success("Note saved.");
        onSuccess?.(leadId);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId || !activeItem || generating) return;
    setGenerating(true);
    try {
      const ok = await persistActive();
      if (!ok) return;
      const pdf = await generateOppositionNoteOnHearingPdf(
        leadId,
        activeItem.conflictingMarkId
      );
      setItems((prev) =>
        prev.map((it) =>
          it.conflictingMarkId === activeItem.conflictingMarkId
            ? {
                ...it,
                notePdfFileKey: pdf.s3Key,
                notePdfFilename: pdf.filename,
                notePdfUrl: pdf.downloadUrl,
              }
            : it
        )
      );
      toast.success("Note PDF generated.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
    }
  };

  const handlePreviewPdf = () => {
    if (!activeItem?.notePdfUrl) return;
    window.open(activeItem.notePdfUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownloadPdf = async () => {
    if (!activeItem?.notePdfUrl || downloading) return;
    setDownloading(true);
    try {
      const result = await downloadFileFromUrlWithFallback(
        activeItem.notePdfUrl,
        activeItem.notePdfFilename ?? "note-on-hearing.pdf"
      );
      if (result === "local") toast.success("PDF saved.");
      else toast.info("Opened the PDF in a new tab.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (items.length === 0 || !activeItem || !activeDraft) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          No applications with a Hearing date set. Save Step 26 first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_NOTE_ON_HEARING_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {items.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {items.map((it, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={it.conflictingMarkId}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors text-xs ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(it.applicationNo ?? "")}
              </button>
            );
          })}
        </div>
      )}

      {/* Fetched details — collapsible accordion */}
      <div className="w-full rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] overflow-hidden">
        <button
          type="button"
          onClick={() => setDetailsOpen((o) => !o)}
          className="flex w-full items-center justify-between px-3 py-2.5 text-[12px] font-medium text-[#222] hover:bg-[#F7F7F7] transition-colors touch-manipulation min-h-[44px] md:min-h-0"
        >
          <span>Fetched Details</span>
          <HiChevronDown
            className="w-4 h-4 text-[#6D6D6D] transition-transform duration-200 shrink-0"
            style={{ transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </button>
        {detailsOpen && (
          <div className="border-t border-[#EEEEEE] flex flex-col gap-3 px-3 py-2.5">
            <div className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[12px] leading-5">
              {[
                { label: "Mark", value: activeItem.mark ?? "" },
                {
                  label: "Selected Class",
                  value:
                    activeItem.selectedClass != null
                      ? String(activeItem.selectedClass)
                      : "",
                },
                {
                  label: "Selected Type of Mark",
                  value: activeItem.typeOfMark ?? "",
                },
                { label: "Goods / Services", value: activeItem.goodsServices ?? "" },
                { label: "User Date", value: activeItem.userDate ?? "" },
                {
                  label: "Date of Opposition Hearing",
                  value: activeItem.dateOfOppositionHearing ?? "",
                },
                {
                  label: "Opposition No.",
                  value: activeItem.oppositionNo ?? "",
                },
                {
                  label: "Name of Conflicting Proprietor",
                  value: activeItem.nameOfConflictingProprietor ?? "",
                },
                {
                  label: "Place of Conflicting Proprietor",
                  value: activeItem.placeOfConflictingProprietor ?? "",
                },
                {
                  label: "Conflicting Mark",
                  value: activeItem.conflictingMark ?? "",
                },
                {
                  label: "Application No. of Conflicting Mark",
                  value: activeItem.applicationNo
                    ? formatTmNoForDisplay(activeItem.applicationNo)
                    : "",
                },
                {
                  label: "Class of Conflicting Mark",
                  value:
                    activeItem.classOfConflictingMark != null
                      ? String(activeItem.classOfConflictingMark)
                      : "",
                },
                {
                  label: "Goods/Services of Conflicting Mark",
                  value: activeItem.goodsServicesOfConflictingMark ?? "",
                },
                {
                  label: "User Date of Conflicting Mark",
                  value: activeItem.userDateOfConflictingMark ?? "",
                },
              ].map((row) => {
                const isEmpty = !row.value;
                return (
                  <Fragment key={row.label}>
                    <div className="flex items-baseline font-medium text-[#222]">
                      {row.label}
                    </div>
                    <div className="flex items-baseline text-[#222]">:</div>
                    <div className="text-[#222] break-words min-w-0">
                      {isEmpty ? (
                        <span className="text-[#6D6D6D]">—</span>
                      ) : (
                        row.value
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-[12px] font-medium text-[#222]">
                  Image of Mark
                </span>
                <div className="w-full min-h-[160px] rounded-[8px] border border-[#EEEEEE] bg-[#F7F7F7] flex items-center justify-center overflow-hidden">
                  {activeItem.imageOfMarkUrl ? (
                    <img
                      src={activeItem.imageOfMarkUrl}
                      alt="Mark"
                      className="max-h-[280px] object-contain"
                    />
                  ) : (
                    <span className="text-xs text-[#6D6D6D] px-2.5 py-2">
                      No image uploaded.
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[12px] font-medium text-[#222]">
                  Image of Conflicting Mark
                </span>
                <div className="w-full min-h-[160px] rounded-[8px] border border-[#EEEEEE] bg-[#F7F7F7] flex items-center justify-center overflow-hidden">
                  {activeItem.conflictingMarkImageUrl ? (
                    <img
                      src={activeItem.conflictingMarkImageUrl}
                      alt="Conflicting mark"
                      className="max-h-[280px] object-contain"
                    />
                  ) : (
                    <span className="text-xs text-[#6D6D6D] px-2.5 py-2">
                      No image uploaded.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label className={LABEL_CLASS}>Similarity Note 1</label>
        <textarea
          value={activeDraft.similarityNote1}
          onChange={(e) => updateDraft({ similarityNote1: e.target.value })}
          rows={4}
          className={TEXTAREA_CLASS}
          placeholder="Note about similarity of marks…"
          disabled={saving}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className={LABEL_CLASS}>Similarity Note 2</label>
        <textarea
          value={activeDraft.similarityNote2}
          onChange={(e) => updateDraft({ similarityNote2: e.target.value })}
          rows={4}
          className={TEXTAREA_CLASS}
          placeholder="Additional similarity note…"
          disabled={saving}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className={LABEL_CLASS}>Other Opposition Note 1</label>
        <textarea
          value={activeDraft.otherOppositionNote1}
          onChange={(e) => updateDraft({ otherOppositionNote1: e.target.value })}
          rows={4}
          className={TEXTAREA_CLASS}
          placeholder="Other note relevant to the opposition…"
          disabled={saving}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className={LABEL_CLASS}>Other Opposition Note 2</label>
        <textarea
          value={activeDraft.otherOppositionNote2}
          onChange={(e) => updateDraft({ otherOppositionNote2: e.target.value })}
          rows={4}
          className={TEXTAREA_CLASS}
          placeholder="Additional note…"
          disabled={saving}
        />
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
          <img src={invoiceGenerate} alt="" className="w-[22px] h-[22px] flex-shrink-0" />
          <button
            type="button"
            onClick={handleGeneratePdf}
            disabled={generating || saving}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60"
          >
            {generating
              ? "Generating…"
              : activeItem.notePdfUrl
                ? "Regenerate PDF"
                : "Generate PDF"}
          </button>
        </div>
        <button
          type="button"
          onClick={handlePreviewPdf}
          disabled={!activeItem.notePdfUrl || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Preview PDF
        </button>
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={!activeItem.notePdfUrl || downloading || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {downloading ? "Downloading…" : "Download PDF"}
        </button>
      </div>
    </form>
  );
}
